import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "../ui/SEO.jsx";

const guides = [
  {
    to: "/guias/quanto-ganha-operario-japao-2026",
    title: "Quanto ganha um operário no Japão em 2026",
    description:
      "Salários reais por hora, valor médio mensal, diferenças por região e por gênero. Sem inflar, sem subestimar — os números honestos.",
    category: "Trabalho",
    readTime: "12 min",
  },
  {
    to: "/guias/vale-a-pena-voltar",
    title: "Vale a pena voltar para o Brasil?",
    description:
      "A pergunta que todo dekassegui faz. Vamos olhar a matemática honesta: o que seu dinheiro vale, o que muda no Brasil, e o que ninguém calcula direito.",
    category: "Decisão de vida",
    readTime: "12 min",
  },
  {
    to: "/guias/kakutei-shinkoku",
    title: "Como fazer kakutei shinkoku",
    description:
      "O ajuste anual de imposto de renda explicado sem termos confusos. Quem precisa fazer, quais documentos juntar, e como deduzir o que dá.",
    category: "Impostos",
    readTime: "10 min",
  },
  {
    to: "/guias/renovacao-visto-zairyu-card",
    title: "Como renovar seu visto e zairyu card",
    description:
      "Renovação do zairyu card, mudança de status, e o que muda com o novo Tokutei Zairyu Card a partir de junho de 2026.",
    category: "Imigração",
    readTime: "10 min",
  },
  {
    to: "/guias/trazer-familia-para-o-japao",
    title: "Como trazer família para o Japão",
    description:
      "O visto de dependente (kazoku taizai) explicado. Requisitos, documentos, custos, e o que muda na vida quando a família chega.",
    category: "Família",
    readTime: "11 min",
  },
  {
    to: "/guias/abrir-conta-banco-japao",
    title: "Como abrir conta bancária no Japão",
    description:
      "Os bancos que aceitam estrangeiro recém-chegado, documentos necessários, e por que Yucho é quase sempre o primeiro passo.",
    category: "Burocracia",
    readTime: "9 min",
  },
  {
    to: "/guias/encontrar-moradia-apato-japao",
    title: "Como encontrar apartamento (apato) no Japão",
    description:
      "Imobiliárias amigas de brasileiro, o sistema do shikikin e reikin, contratos, e como evitar a recusa por ser estrangeiro.",
    category: "Moradia",
    readTime: "11 min",
  },
  {
    to: "/guias/hakken-vs-seishain",
    title: "Hakken vs seishain: qual contrato escolher",
    description:
      "A diferença entre empreiteira e funcionário efetivo, com números reais de salário, benefícios e estabilidade.",
    category: "Trabalho",
    readTime: "9 min",
  },
  {
    to: "/guias/gaimen-kirikae",
    title: "Carteira de motorista no Japão",
    description:
      "Conversão da habilitação brasileira (gaimen kirikae). Documentos, tradução, prova teórica e prática, custos.",
    category: "Burocracia",
    readTime: "8 min",
  },
  {
    to: "/guias/shakai-vs-kokumin",
    title: "Shakai hoken vs kokumin hoken",
    description:
      "Qual seguro é melhor quando você muda de emprego, vira autônomo, ou recebe proposta de contrato sem cobertura.",
    category: "Trabalho",
    readTime: "7 min",
  },
  {
    to: "/guias/mandar-dinheiro-para-o-brasil",
    title: "Como mandar dinheiro para o Brasil",
    description:
      "Comparativo entre Wise, Brastel, GoRemit, banco tradicional. Qual realmente entrega mais reais por iene em 2026.",
    category: "Remessas",
    readTime: "9 min",
  },
  {
    to: "/guias/trabalhar-legal-japao-tipos-de-visto",
    title: "Trabalhar legal no Japão: os tipos de visto",
    description:
      "Quais vistos permitem trabalho, qual é o seu, e quais são as restrições reais de cada um. Para descendentes, cônjuges, qualificados e operários.",
    category: "Imigração",
    readTime: "11 min",
  },
  {
    to: "/guias/glossario-japones-portugues",
    title: "Glossário japonês-português para dekassegui",
    description:
      "Termos que todo brasileiro no Japão escuta no trabalho, na fábrica, na imigração, na prefeitura e no banco — traduzidos e explicados.",
    category: "Referência",
    readTime: "14 min",
  },
];

export default function Guias() {
  return (
    <div>
      <SEO
        title="Guias"
        description="Guias práticos para brasileiros no Japão: kakutei shinkoku, gaimen kirikae, shakai hoken, remessas."
        path="/guias"
      />
      
      <div className="text-[11px] tracking-[0.18em] uppercase text-accent mb-4 font-semibold">
        Leitura
      </div>
      <h1 className="font-serif font-medium text-4xl md:text-5xl leading-tight tracking-tight mb-4">
        Guias
      </h1>
      <p className="text-lg text-ink-soft leading-relaxed max-w-xl mb-10">
        Explicações práticas sobre os processos que mais geram dúvida.
        Escritos por quem já passou por eles, atualizados quando a lei muda.
      </p>

      <div className="space-y-3">
        {guides.map((g) => (
          <Link
            key={g.to}
            to={g.to}
            className="block p-6 md:p-7 rounded-2xl border border-border bg-surface hover:border-accent transition-colors no-underline text-ink group"
          >
            <div className="flex items-center gap-3 mb-3 text-xs font-mono text-ink-soft">
              <span className="text-accent uppercase tracking-wider font-semibold">
                {g.category}
              </span>
              <span>·</span>
              <span>{g.readTime}</span>
            </div>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h2 className="font-serif text-2xl font-medium mb-2 group-hover:text-accent transition-colors">
                  {g.title}
                </h2>
                <p className="text-[15px] text-ink-soft leading-relaxed">
                  {g.description}
                </p>
              </div>
              <ArrowRight size={20} className="text-ink-soft group-hover:text-accent transition-colors shrink-0 mt-1" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
